<template>
    <div>
        <div v-if="deletingScheduling">
            <UpdateRuleSelector :onSelect="handleUpdateRuleSelection" :onCancel="handleSchedulingDeleteCancel" title="Remover agendamento" />
        </div>
        <div v-else class="scheduling-view container-fluid">
            <span class="calendar-loading" v-if="loadingProducts">
                <i class="material-icons loading-icon-size">cached</i>
            </span>
            <div class="row mb-3">
                <div class="col-md-9">
                    <h1 class="page-title">
                        <SidebarButton :dashboardUrl="dashboardUrl" />
                        {{ componentTitle }}
                    </h1>
                </div>                
                <div class="col-md-3">
                    <button v-if="shouldDisplayCreateButton" class="btn btn-primary btn-block" v-on:click="handleCreate">Novo agendamento</button>
                </div>
            </div>
            <div class="row mb-2">
                <div class="col-sm-8 col-md-8 col-lg-6 col-xl-5 form-group">
                    <Multiselect
                        v-if="shouldDisplayProductSelector"
                        v-model="selectedProductId"
                        :options="availableProductsOptions"
                        mode="single"
                        searchable
                        :hideSelected="false"
                        :native="false"
                        ref="multiselect"
                        :loading="loadingProducts"
                        :canClear="false"
                        :canDeselect="false"
                    ></Multiselect>
                </div>
                <div class="col-sm-4 col-lg-2 col-lg-3 col-xl-2">
                    <select v-if="shouldDisplayViewToggle" class="form-control" v-model="displayMode">
                        <option value="month">Mês</option>
                        <option value="week">Semana</option>
                    </select>
                </div>
            </div>
            <div class="row">
                <div class="col">
                    <SchedulingForm
                        v-if="shouldDisplayForm"
                        :product="selectedProduct"
                        :scheduling="currentScheduling"
                        :validation="validation"
                        :on-submit="handleSchedulingSubmit"
                        :on-cancel="cancelForm"
                    />
                    <WeeklyView
                        v-else-if="displayWeek"
                        :initialDate="currentIntervalFirstDate"
                        :minuteSetInterval="minuteSetInterval"
                        :provideItems="handleProvideItems"
                        :onWeekChange="handleWeekChange"
                    />
                    <CalendarView
                        v-else
                        :initial-month="currentMonth"
                        :reference-date="now"
                        :on-month-change="handleMonthChange"
                        :render-date="renderDate"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { requestJson, requestAllPagesJsonWithCallback } from '../helpers';
import { newDate } from '../../common/calendar';
import CalendarView from './CalendarView';
import SchedulingForm from './SchedulingForm';
import UpdateRuleSelector from './UpdateRuleSelector';
import WeeklyView from './WeeklyView';
import DisplayCalendar from './mixins/DisplayCalendar';
import SidebarButton from './SidebarButton';
import Multiselect from "@vueform/multiselect/dist/multiselect.vue2.js";

export default {
    mixins: [
        DisplayCalendar
    ],
    props: {
        availableProductsUrl: String,
        schedulingDatesUrl: String,
        componentTitle: String,
        minuteSetInterval: Number,
        dashboardUrl: String,
    },
    components: {
        CalendarView,
        SchedulingForm,
        UpdateRuleSelector,
        WeeklyView,
        SidebarButton,
        Multiselect,
    },
    data(){
        return {
            availableProducts: [],
            loadingProducts: false,
            selectedProductId: null,
            currentScheduling: null,
            deletingScheduling: null,
            validation: null,
            displayMode: 'month',
            loadingProducts: false,
        };
    },
    computed: {
        shouldDisplayForm(){
            return this.currentScheduling !== null;
        },
        shouldDisplayCreateButton(){
            return this.selectedProduct != null && this.currentScheduling === null;
        },
        shouldDisplayProductSelector(){
            return this.currentScheduling === null;
        },
        shouldDisplayViewToggle(){
            return this.currentScheduling === null;
        },
        selectedProduct(){
            return this.availableProducts.find(prod => prod.id === this.selectedProductId);
        },
        currentIntervalFirstDate(){
            return this.currentInterval ? this.currentInterval[0] : null;
        },
        displayWeek(){
            return this.displayMode === 'week';
        },
        availableProductsOptions(){
            return this.availableProducts.map(prod => ({ value: prod.id, label: prod.name }));
        },
    },
    watch: {
        selectedProductId: {
            handler() {
                this.handleSelectedProductChange();
            },
            immediate: true,
        },
    },
    methods: {
        async requestAvailableProducts(){
            this.loadingProducts = true;
            await requestAllPagesJsonWithCallback(this.availableProductsUrl, null, (products, done) => {
                this.availableProducts = products.filter(prod => (prod.selling_mode !== 'without_date' && prod.active));

                if(this.selectedProductId == null && this.availableProducts.length > 0){
                    this.selectedProductId = this.availableProducts[0].id;
                }
            });
            this.loadingProducts = false;
        },

        handleSelectedProductChange(){
            this.monthlyIndex = {};
            this.validation = null;

            this.requestIntervalSchedulings();
        },

        requestMonthData(){
            this.requestIntervalSchedulings();
        },

        requestIntervalSchedulings(){
            if(this.selectedProduct == null || this.currentInterval == null)
                return;

            const [
                { dateString: from },
                { dateString: to }
            ] = this.currentInterval;

            const params = { from, to, product_ids: [this.selectedProduct.id] };
            requestJson(this.schedulingDatesUrl, params)
                .then(json => this.receiveMonthSchedulings(json))
                .catch(ex => null);
        },
        receiveMonthSchedulings(arr){
            const index = {};

            for(let sch of arr){
                const { applies_at } = sch;

                if(index[applies_at] == null){
                    index[applies_at] = [];
                }

                index[applies_at].push(sch);
            }

            this.monthlyIndex = index;
        },

        renderDate(date, createElement){
            const schedulings = this.monthlyIndex[date.dateString] || [];

            const lis = schedulings.map(sch => this.renderScheduling(sch, 'li', createElement));
            return createElement('ul', {'class': 'scheduling-list'}, lis);
        },

        renderScheduling(sch, tag, createElement){
            const tariffGroup = sch.tariff_group ?? this.selectedProduct.tariff_groups.find(group => group.is_default);
            
            const tariffGroupNameArray = fun => sch.id_tariff_group ? [tariffGroup.name] : ['Padrão (', fun(tariffGroup.name), ')' ];

            const schedulingTitle = ([ tariffGroup ? tariffGroupNameArray(str => str).join('') : '', sch.formatted_consumable_period]).join(' ').trim();

            return createElement(tag, { domProps: { title: schedulingTitle }, 'class': 'scheduling-list-item' }, [
                createElement('div', { 'class': 'scheduling-content' }, [
                    createElement('span', tariffGroup ? tariffGroupNameArray(str => createElement('em', str)) : ''),
                    createElement('div', {'class': 'font-weight-normal'}, sch.formatted_consumable_period)
                ]),
                createElement('div', { 'class': 'scheduling-actions' }, [
                    ...(sch.tariff_group?.deleted_at ? [
                        createElement('div', { 'class': 'scheduling-status' }, [
                            createElement('i', { domProps: {title: 'Este agendamento está atrelado a um grupo de tarifas arquivado. Selecione outro grupo de tarifas'}, 'class': 'material-icons' }, 'warning')
                        ])
                    ]:[]),
                    createElement('span', { 'class': 'scheduling-options' }, [
                        createElement('a', {
                            domProps: { href: 'javascript:void(0)', title: 'Editar' },
                            'class': 'material-icons',
                            on: { click: () => this.handleSchedulingEdit(sch) }
                        }, 'edit'),
                        createElement('a', {
                            domProps: { href: 'javascript:void(0)', title: 'Excluir' },
                            'class': 'material-icons',
                            on: { click: () => this.handleSchedulingDelete(sch) }
                        }, 'delete')
                    ])
                ]),
            ]);
        },

        handleCreate(){
            this.currentScheduling = {};
        },
        handleSchedulingEdit(sch){
            this.currentScheduling = sch;
        },
        cancelForm(){
            this.currentScheduling = null;
        },

        handleSchedulingSubmit(values){
            const promise = values.update_url != null ? requestJson(values.update_url, values, 'PATCH') : requestJson(this.selectedProduct.store_scheduling_url, values, 'POST');
            promise.then(json => this.receiveSchedulingResponse(json))
                .catch(ex => null);

            this.currentScheduling = { ...this.currentScheduling, ...values };
        },
        receiveSchedulingResponse(json){
            if(json.status === 'OK'){
                this.currentScheduling = null;
                this.validation = null;

                this.requestIntervalSchedulings();
            }
            else{
                this.handleErrorResponse(json);
            }
        },
        handleErrorResponse(json){
            this.validation = {
                ...json.errors,
                error_dates: json.error_dates || null
            };
        },

        handleSchedulingDelete(sch){
            this.deletingScheduling = sch;
        },
        handleSchedulingDeleteCancel(sch){
            this.deletingScheduling = null;
        },
        handleUpdateRuleSelection(update_rule){
            requestJson(this.deletingScheduling.delete_url, { update_rule }, 'DELETE')
                .then(json => this.receiveDeleteResponse(json))
                .catch(ex => null);
        },
        receiveDeleteResponse(json){
            if(json.status === 'OK'){
                this.deletingScheduling = null;
                this.validation = null;

                this.requestIntervalSchedulings();
            }
            else{
                this.handleErrorResponse(json);
            }
        },

        handleProvideItems(date, createElement){
            const schedulings = this.monthlyIndex[date.dateString] || [];

            return schedulings.map(sch => ({
                key: sch.id,
                full: sch.consumable_from_minutes === null,
                from: sch.consumable_from_minutes,
                to: sch.consumable_to_minutes,
                render: createElement => this.renderScheduling(sch, 'div', createElement)
            }));
        },
        handleWeekChange(weekdays){
            const { fullDate: first } = weekdays[0];
            const { fullDate: last } = weekdays[weekdays.length - 1];

            const { year, month } = first;
            this.currentMonth = [year, month];
            this.currentInterval = [first, last];

            this.requestIntervalSchedulings();
        }
    },
    async mounted(){
        await this.requestAvailableProducts();
    }
}
</script>

<style lang="scss">
.scheduling-list {
    padding: 0;
    list-style: none;
}
.scheduling-list-item {
    display: flex;
    box-sizing: border-box;
    margin-top: 3px;
    padding: 5px;
    align-items: center;
    border-radius: 5px;
    font-weight: bold;
    font-size: .85rem;
    background-color: #131552;
    color: #fff;
    flex-wrap: wrap;
    align-content: flex-start;
    text-align: left;
    overflow: hidden;

    .scheduling-content {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        flex: 1 1;
    }

    .scheduling-status {
        text-align: right;

        i {
            font-size: 1.05rem;
        }
    }

    .scheduling-options {
        text-align: right;
        visibility: hidden;

        a {
            font-size: 1.05rem;
            color: #fff;
        }
    }

    &:hover .scheduling-options {
        visibility: visible;
    }
}
</style>
